// components/PostsPage/PostPageContainer.tsx
import { useState, useMemo } from "react";
import { Container } from "@/components/Container";
import { useToast } from "@/hooks/use-toast";
import { usePostsQuery } from "@/api/posts";
import { useQueryClient } from "@tanstack/react-query";

// Import subcomponents
import PostTabs from "./PostTabs";

interface PostPageContainerProps {
  personaId: string;
  personaData: any;
}

function PostPageContainer({ personaId, personaData }: PostPageContainerProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // State management
  const [sortBy, setSortBy] = useState("newest");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  // Convert UI sort option to API sort order
  const sortOrder = useMemo(
    () => (sortBy === "newest" ? "desc" : "asc"),
    [sortBy],
  );

  // Query for all posts with server-side sorting
  const {
    data: postsData,
    isLoading: isPostsLoading,
    refetch: refetchPosts,
  } = usePostsQuery(
    personaId,
    50,
    activeTab !== "all" ? activeTab : undefined,
    sortOrder,
  );

  // Refresh posts
  const refreshPosts = async () => {
    try {
      setIsRefreshing(true);
      await queryClient.invalidateQueries({
        queryKey: ["posts", personaId],
      });
      await refetchPosts();
      toast({ description: "Posts refreshed" });
    } catch (error) {
      console.error(`Error refreshing posts:`, error);
      toast({
        description: `Failed to refresh posts`,
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Use posts directly from the API - server already handles sorting
  const filteredPosts = useMemo(() => {
    if (!postsData?.data) return [];
    return postsData.data;
  }, [postsData]);

  return (
    <Container className="max-w-5xl px-4 sm:px-6 mx-auto">
      {/* Main content */}
      <div className="space-y-4">
        {/* Tabs for All, Media Posts, Media Evaluation, and Posts */}
        <PostTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          sortBy={sortBy}
          onSortChange={setSortBy}
          isRefreshing={isRefreshing}
          isPostsLoading={isPostsLoading}
          onRefresh={refreshPosts}
          posts={filteredPosts}
          personaId={personaId}
          personaData={personaData}
        />
      </div>
    </Container>
  );
}

export default PostPageContainer;
